import { useState, useRef, useEffect, type FormEvent } from 'react';
import { Mail, Phone, MapPin, Send, Facebook, Instagram, Twitter, Linkedin } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import '@/App.css';
import Navigation from '@/components/nav';
import FooterSection from '@/components/footer';
import { toast } from 'sonner';
import HeroSection from '@/components/HeroSection';

const API_BASE = import.meta.env.VITE_API_BASE || "https://qwaqwatalentshow.co.za";;

interface ContactFormData {
    name: string;
    email: string;
    subject: string;
    message: string;
}

function ContactPage() {
    const sectionRef = useRef<HTMLElement>(null);
    const [isVisible, setIsVisible] = useState(false);
    const [formData, setFormData] = useState<ContactFormData>({
        name: '',
        email: '',
        subject: '',
        message: '',
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                    observer.disconnect();
                }
            },
            { threshold: 0.15 }
        );

        if (sectionRef.current) observer.observe(sectionRef.current);
        return () => observer.disconnect();
    }, []);

    const handleInputChange = (field: keyof ContactFormData, value: string) => {
        setFormData(prev => ({
            ...prev,
            [field]: value,
        }));
    };

    const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (!formData.name || !formData.email || !formData.subject || !formData.message) {
            toast.error('Please fill in all fields');
            return;
        }

        setIsSubmitting(true);
        try {
            const res = await fetch(`${API_BASE}/api/v1/contact/submit`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(formData),
            });

            const result = await res.json();

            if (res.ok && result.success) {
                setSubmitted(true);
                toast.success(result.message || 'Message sent successfully! We\'ll get back to you soon.');

                // Reset form
                setFormData({
                    name: '',
                    email: '',
                    subject: '',
                    message: '',
                });

                // Reset submitted state after 3 seconds
                setTimeout(() => {
                    setSubmitted(false);
                }, 3000);
            } else {
                toast.error(result.message || 'Failed to send message. Please try again later.');
            }
        } catch (error) {
            console.error('Error submitting contact form:', error);
            toast.error('Network error. Please check your connection and try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const contactInfo = [
        {
            icon: Mail,
            title: 'Email',
            details: 'qwa2talentshow@gmail.com',
            subtext: 'We\'ll respond within 24 hours',
        },
        {
            icon: Phone,
            title: 'Phone',
            details: '+27 (0) 67 835 9550',
            subtext: 'Available Monday - Friday, 9am - 5pm',
        },
        {
            icon: MapPin,
            title: 'Location',
            details: '1105 Bophelong Village, Phuthaditjhaba, Free State, South Africa',
            subtext: 'Visit us at our main office',
        },
    ];

    const socialLinks = [
        { icon: Facebook, label: 'Facebook', link: "https://www.facebook.com/profile.php?id=100083594971203" },
    ];

    return (
        <div className="min-h-screen bg-white">
            <Navigation />
            <main>
                {/* HERO SECTION */}
                <HeroSection title="Get In Touch" description="Contact Us" 
                long="Have questions about the talent show, sponsorship opportunities, or just want to reach out? We'd love to hear from you. Send us a message and we'll respond as soon as possible." />
      

                {/* CONTACT INFO CARDS */}
                <section className="relative py-20 md:py-28 bg-white overflow-hidden">
                    <div className="absolute top-10 left-10 w-32 h-32 rounded-full bg-QWA2_TALENT_SHOW-gold/10 blur-3xl" />
                    <div className="absolute bottom-10 right-10 w-40 h-40 rounded-full bg-QWA2_TALENT_SHOW-red/10 blur-3xl" />

                    <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="grid gap-8 md:grid-cols-3">
                            {contactInfo.map((info, index) => {
                                const Icon = info.icon;
                                return (
                                    <div
                                        key={index}
                                        className="rounded-3xl bg-gradient-to-br from-QWA2_TALENT_SHOW-gold/10 to-transparent p-8 border border-QWA2_TALENT_SHOW-gold/20 hover:border-QWA2_TALENT_SHOW-gold/50 transition-all"
                                    >
                                        <div className="flex items-center justify-center w-16 h-16 rounded-2xl bg-QWA2_TALENT_SHOW-gold/20 mb-6">
                                            <Icon className="w-8 h-8 text-QWA2_TALENT_SHOW-gold" />
                                        </div>
                                        <h3 className="text-2xl font-black text-QWA2_TALENT_SHOW-black mb-2">{info.title}</h3>
                                        <p className="text-lg font-bold text-QWA2_TALENT_SHOW-gold mb-2">{info.details}</p>
                                        <p className="text-sm text-QWA2_TALENT_SHOW-gray">{info.subtext}</p>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </section>

                {/* CONTACT FORM & INFO */}
                <section ref={sectionRef} className="relative py-20 md:py-28 bg-gradient-to-b from-white to-gray-100 overflow-hidden">
                    <div className="absolute top-24 right-10 w-40 h-40 rounded-full bg-QWA2_TALENT_SHOW-red/20 blur-3xl" />
                    <div className="absolute bottom-20 left-10 w-48 h-48 rounded-full bg-QWA2_TALENT_SHOW-gold/10 blur-3xl" />

                    <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className={`mb-14 text-center transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
                            <h2 className="text-3xl md:text-4xl font-black text-QWA2_TALENT_SHOW-black">
                                Send Us a Message
                            </h2>
                            <p className="mt-4 text-base md:text-lg text-QWA2_TALENT_SHOW-gray max-w-2xl mx-auto">
                                Fill out the form below and we'll get back to you within one business day.
                            </p>
                        </div>

                        <div className="grid lg:grid-cols-2 gap-12 gap-y-8">
                            {/* FORM */}
                            <div className={`transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
                                <div className="rounded-3xl bg-white p-8 md:p-12 shadow-lg border border-QWA2_TALENT_SHOW-gold/10">
                                    <form onSubmit={handleSubmit} className="space-y-6">
                                        {!submitted ? (
                                            <>
                                                {/* NAME */}
                                                <div className="space-y-2">
                                                    <label className="text-sm font-bold uppercase tracking-wider text-QWA2_TALENT_SHOW-black">
                                                        Full Name
                                                    </label>
                                                    <Input
                                                        type="text"
                                                        placeholder="Your name"
                                                        value={formData.name}
                                                        onChange={(e) => handleInputChange('name', e.target.value)}
                                                        className="w-full border-2 border-QWA2_TALENT_SHOW-gold/20 focus:border-QWA2_TALENT_SHOW-gold rounded-2xl py-3 px-4"
                                                        required
                                                    />
                                                </div>

                                                {/* EMAIL */}
                                                <div className="space-y-2">
                                                    <label className="text-sm font-bold uppercase tracking-wider text-QWA2_TALENT_SHOW-black">
                                                        Email Address
                                                    </label>
                                                    <Input
                                                        type="email"
                                                        placeholder="your@email.com"
                                                        value={formData.email}
                                                        onChange={(e) => handleInputChange('email', e.target.value)}
                                                        className="w-full border-2 border-QWA2_TALENT_SHOW-gold/20 focus:border-QWA2_TALENT_SHOW-gold rounded-2xl py-3 px-4"
                                                        required
                                                    />
                                                </div>

                                                {/* SUBJECT */}
                                                <div className="space-y-2">
                                                    <label className="text-sm font-bold uppercase tracking-wider text-QWA2_TALENT_SHOW-black">
                                                        Subject
                                                    </label>
                                                    <Input
                                                        type="text"
                                                        placeholder="What is this about?"
                                                        value={formData.subject}
                                                        onChange={(e) => handleInputChange('subject', e.target.value)}
                                                        className="w-full border-2 border-QWA2_TALENT_SHOW-gold/20 focus:border-QWA2_TALENT_SHOW-gold rounded-2xl py-3 px-4"
                                                        required
                                                    />
                                                </div>

                                                {/* MESSAGE */}
                                                <div className="space-y-2">
                                                    <label className="text-sm font-bold uppercase tracking-wider text-QWA2_TALENT_SHOW-black">
                                                        Message
                                                    </label>
                                                    <textarea
                                                        placeholder="Your message here..."
                                                        value={formData.message}
                                                        onChange={(e) => handleInputChange('message', e.target.value)}
                                                        className="w-full border-2 border-QWA2_TALENT_SHOW-gold/20 focus:border-QWA2_TALENT_SHOW-gold rounded-2xl py-3 px-4 focus:outline-none focus:ring-0 resize-none"
                                                        rows={5}
                                                        required
                                                    />
                                                </div>

                                                {/* SUBMIT */}
                                                <Button
                                                    type="submit"
                                                    disabled={isSubmitting}
                                                    className="w-full bg-gradient-to-r from-QWA2_TALENT_SHOW-red to-QWA2_TALENT_SHOW-gold text-white font-bold uppercase tracking-wider py-3 rounded-2xl hover:opacity-90 transition flex items-center justify-center gap-2 disabled:opacity-50"
                                                >
                                                    <Send className="w-5 h-5" />
                                                    {isSubmitting ? 'Sending...' : 'Send Message'}
                                                </Button>
                                            </>
                                        ) : (
                                            <div className="text-center py-12 space-y-4">
                                                <div className="flex items-center justify-center w-16 h-16 rounded-full bg-green-500/20 mx-auto">
                                                    <span className="text-4xl text-green-600">✓</span>
                                                </div>
                                                <p className="text-2xl font-black text-QWA2_TALENT_SHOW-black">Thank You!</p>
                                                <p className="text-QWA2_TALENT_SHOW-gray">
                                                    Your message has been sent successfully. We'll get back to you soon.
                                                </p>
                                            </div>
                                        )}
                                    </form>
                                </div>
                            </div>

                            {/* INFO SIDEBAR */}
                            <div className={`flex flex-col gap-8 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                                style={{ transitionDelay: '100ms' }}>
                                {/* QUICK INFO */}
                                <div className="rounded-3xl bg-gradient-to-br from-QWA2_TALENT_SHOW-red/10 to-transparent p-8 border border-QWA2_TALENT_SHOW-red/20">
                                    <h3 className="text-2xl font-black text-QWA2_TALENT_SHOW-black mb-4">Quick Response</h3>
                                    <p className="text-QWA2_TALENT_SHOW-gray mb-6">
                                        We aim to respond to all inquiries within 24 business hours. For urgent matters, please call us directly.
                                    </p>
                                    <ul className="space-y-3">
                                        <li className="flex items-start gap-3">
                                            <span className="text-QWA2_TALENT_SHOW-gold font-bold mt-1">✓</span>
                                            <span className="text-QWA2_TALENT_SHOW-black text-sm">Email support available 24/7</span>
                                        </li>
                                        <li className="flex items-start gap-3">
                                            <span className="text-QWA2_TALENT_SHOW-gold font-bold mt-1">✓</span>
                                            <span className="text-QWA2_TALENT_SHOW-black text-sm">Phone support Monday - Friday</span>
                                        </li>
                                        <li className="flex items-start gap-3">
                                            <span className="text-QWA2_TALENT_SHOW-gold font-bold mt-1">✓</span>
                                            <span className="text-QWA2_TALENT_SHOW-black text-sm">Follow us on social media for updates</span>
                                        </li>
                                    </ul>
                                </div>

                                {/* SOCIAL MEDIA */}
<div className="rounded-3xl bg-gradient-to-br from-blue-500/15 via-blue-400/10 to-transparent p-8 border border-blue-400/30">
    
    <h3 className="text-2xl font-black text-blue-600 mb-2">
        Follow Us on Facebook
    </h3>

    <p className="text-sm text-blue-500/80 mb-1 font-semibold">
        2.5K likes • 2.5K followers
    </p>

    <p className="text-sm text-gray-700 mb-6 italic">
        "We're not doing it for ourselves but for the coming generations"
    </p>

    <button
        onClick={() => {
            const facebookLink = "https://www.tiktok.com/@qwa2.talent.show?_t=ZS-8zSEbze4LNl&_r=1"; // replace with your page link
            window.open(facebookLink, "_blank");
        }}
        className="flex items-center justify-center gap-2 p-5 w-full rounded-2xl bg-blue-600 hover:bg-blue-700 transition text-white font-semibold cursor-pointer"
    >
        {/* Facebook Icon */}
        <svg
            className="w-5 h-5"
            fill="currentColor"
            viewBox="0 0 24 24"
        >
            <path d="M22 12a10 10 0 10-11.5 9.95v-7.05h-2.2V12h2.2v-1.7c0-2.18 1.3-3.4 3.3-3.4.96 0 1.96.17 1.96.17v2.16h-1.1c-1.08 0-1.42.67-1.42 1.36V12h2.42l-.39 2.9h-2.03v7.05A10 10 0 0022 12z" />
        </svg>

        <span className="text-sm">Visit Facebook Page</span>
    </button>
</div>
                                {/* FAQ */}
                                <div className="rounded-3xl bg-white p-8 border border-QWA2_TALENT_SHOW-gold/20">
                                    <h3 className="text-2xl font-black text-QWA2_TALENT_SHOW-black mb-4">FAQ</h3>
                                    <ul className="space-y-3 text-sm text-QWA2_TALENT_SHOW-gray">
                                        <li className="flex gap-2">
                                            <span className="text-QWA2_TALENT_SHOW-gold font-bold">Q:</span>
                                            <span>How do I register for the talent show?</span>
                                        </li>
                                        <li className="flex gap-2">
                                            <span className="text-QWA2_TALENT_SHOW-gold font-bold">Q:</span>
                                            <span>What are the entry requirements?</span>
                                        </li>
                                        <li className="flex gap-2">
                                            <span className="text-QWA2_TALENT_SHOW-gold font-bold">Q:</span>
                                            <span>Can I sponsor the event?</span>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <FooterSection />
            </main>
        </div>
    );
}

export default ContactPage;
